type EventMap<T extends EventTarget> = T extends MediaQueryList
  ? MediaQueryListEventMap
  : T extends Document
  ? DocumentEventMap
  : T extends Window
  ? WindowEventMap
  : T extends HTMLElement
  ? HTMLElementEventMap
  : Record<string, Event>;

type EventTypes<T extends EventTarget> = keyof EventMap<T> & string;
type EventValue<T extends EventTarget, K extends EventTypes<T>> = Extract<EventMap<T>[K], Event>;

export function addHtmlListener<E extends EventTarget, T extends EventTypes<E>>(element: E, type: T, listener: (this: E, ev: EventValue<E, T>) => any, options?: boolean | AddEventListenerOptions): () => void {
  element.addEventListener(type, listener as any, options);
  return () => element.removeEventListener(type, listener as any, options);
}

export const copyToClipboard = async (str: string): Promise<boolean> => {
  try {
    navigator.clipboard.writeText(str);
    return true;
  } catch (e) {
    console.warn("Failed to copy to clipboard: ", e)
    return false;
  }
};

export const copyFromClipboard = async (): Promise<string> => {
  try {
    return await navigator.clipboard?.readText() || ""; // not supported by firefox
  } catch (e) {
    console.warn("Failed to read clipboard: ", e)
    return "";
  }
};

export function isIOS() {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream) {
    return true;
  } else {
    return isIpadOS();
  }
}

// export function isIOS(): boolean {
//   // const isIosOld = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
//   const isAppleDevice = navigator.userAgent.includes('Macintosh');
//   const isTouchScreen = navigator.maxTouchPoints >= 1; // true for iOS 13 (and hopefully beyond)
//   const isIOS = isAppleDevice && isTouchScreen;
//   return isIOS;
// }

export function isTouchScreen() {
  return navigator.maxTouchPoints && navigator.maxTouchPoints > 2;
}

export function isIpadOS() {
  return navigator.maxTouchPoints &&
    isTouchScreen() &&
    navigator.userAgent.match(/Mac/)
}

export function isSafari(): boolean {
  // const isIosOld = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
  return navigator.userAgent.includes('Safari');
}

export async function showFileInput(options?: { accept?: "image" }): Promise<File | undefined> {
  console.log("ShowFileInput");
  return new Promise<File | undefined>((resolve, reject) => {
    const input = document.createElement('input');
    console.log("ShowFileInput.create");
    input.type = "file";
    if (options?.accept)
      input.accept = options.accept + "/*";

    input.onchange = async (ev: any) => {
      const file = ev?.target?.files?.[0];
      console.log("ShowFileInput: ", file?.name);
      if (options?.accept) {
        const imageType = /^image\//;
        if (!file || !imageType.test(file.type)) {
          console.log("ShowFileInput.reject ", file?.name);
          reject("Invalid file!");
        }
      }

      console.log("ShowFileInput.resolve ", file?.name);
      resolve(file);
    }
    input.onerror = (ev, source, lineno) => {
      console.log("ShowFileInput.reject ", ev, source, lineno);
      reject("Invalid file!");
    }
    input.onended = (_ev) => {
      console.log("OnEnded!");
    }
    input.onblur = (_ev) => {
      console.log("onblur !");
    }
    input.onfocus = (_ev) => {
      console.log("onfocus !");
    }
    input.onclick = (_ev) => {
      console.log("onclick  !");
    }

    setTimeout(function () {
      input.click();
      console.log("ShowFileInput.click");
    }, 300);
  })
}

export function getScrollParent(node: HTMLElement | undefined | null): HTMLElement | undefined {
  if (!node) {
    return;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return getScrollParent(node.parentNode as HTMLElement);
  }
}

export function createBlankUrlWindow(): Window | null {
  const windowRef = window.open("about:blank", "_blank", "noreferrer")
  return windowRef;
}

export function openBlankUrl(url: string): Window | null {
  return window.open(url, "_blank", "noreferrer"); // doesn't work on ios in async function})
}

function reflow(r: HTMLDivElement | null) {
  return r?.offsetWidth;
}

export function reflowRef(r: HTMLDivElement | null) {
  reflow(r);
}